// frontend/src/pages/PlatformScheduler.js
//
// Renders /instagram-scheduler and /tiktok-scheduler from
// data/platform-schedulers.json.
//
// Targets (docs/keyword-research/clusters/scheduling-howto.csv):
//   tiktok scheduling tool     100-1K   $11.71  comp 1
//   instagram scheduling tool  1K-10K   $7.41   comp 5
// Plus `ig bulk posting tool` (pos 14.5) and `tk bulk posting tool` (pos 9.57)
// from Search Console, which is where this pair started before the keyword data
// showed the scheduling terms are worth 10-50x more than the bulk phrasing.
//
// URL choice is deliberate: /bulk-posting/instagram would have signalled "bulk
// posting for Instagram", not "Instagram scheduling tool". The URL should match
// the intent being targeted.
//
// One component, but the CONTENT per platform is genuinely distinct — a template
// with the platform name swapped would be thin content competing with itself.
//
// Plain <table className='table'>, NOT react-bootstrap <Table> (React 19 crash,
// cdab6060).
import '../custom.css'

import React from 'react'
import { Container, Row, Col, Image, Button, Card, Accordion } from 'react-bootstrap'
import { useLocation } from 'react-router-dom'
import { CheckCircleFill, ExclamationTriangleFill } from 'react-bootstrap-icons'

import Seo, { SITE_URL, BRAND } from '../components/Seo'
import platforms from '../data/platform-schedulers.json'
import personas from '../data/personas.json'
import NotFound from './NotFound'

/** Content carries inline links, so render as HTML rather than plain text. */
const Html = ({ as: Tag = 'span', html, ...rest }) => (
  // eslint-disable-next-line react/no-danger
  <Tag {...rest} dangerouslySetInnerHTML={{ __html: html }} />
)

export default function PlatformScheduler () {
  // Derived from the path, NOT useParams — these routes are static
  // (/instagram-scheduler, /tiktok-scheduler) with no :param, so useParams
  // returned nothing and every page silently rendered the 404 instead.
  const { pathname } = useLocation()
  const slug = pathname.replace(/^\/+|\/+$/g, '')
  const p = platforms.find(x => x.slug === slug)
  if (!p) return <NotFound />

  const faqLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: p.faqs.map(([q, a]) => ({
      '@type': 'Question',
      name: q,
      // Strip markup — schema answers must be plain text.
      acceptedAnswer: { '@type': 'Answer', text: a.replace(/<[^>]+>/g, '') },
    })),
  }

  const howToLd = {
    '@context': 'https://schema.org',
    '@type': 'HowTo',
    name: `How to schedule ${p.platform} posts`,
    step: p.steps.map(([name, text], i) => ({
      '@type': 'HowToStep',
      position: i + 1,
      name,
      text: text.replace(/<[^>]+>/g, ''),
    })),
  }

  return (
    <>
      <Seo
        title={p.seoTitle || `${p.platform} Scheduler — Schedule ${p.platform} Posts in Advance`}
        description={p.seoDescription || p.sub.replace(/<[^>]+>/g, '').slice(0, 155)}
        path={`/${p.slug}`}
        jsonLd={[faqLd, howToLd]}
      />

      {/* ───────── HERO ───────── */}
      <section className='full-bleed py-5 bg-primary-soft'>
        <Container fluid='lg'>
          <Row className='align-items-center g-5'>
            <Col md={6}>
              <h1 className='display-5 fw-bold mb-3'>{p.h1}</h1>
              <p className='lead mb-4'>{p.sub}</p>
              <Button variant='primary' size='lg' href='/register' className='me-2 mb-2'>
                Start free trial
              </Button>
              <Button variant='outline-primary' size='lg' href='/plans' className='mb-2'>
                See plans
              </Button>
            </Col>
            <Col md={6}>
              <Image src={p.hero} alt={p.heroAlt} fluid className='shadow-card rounded-4' />
            </Col>
          </Row>
        </Container>
      </section>

      {/* ───────── DEFINITION (snippet target) ───────── */}
      <section className='py-5'>
        <Container style={{ maxWidth: 760 }}>
          <Html as='p' className='fs-5' html={p.intro} />
          <Html as='p' className='text-muted fs-5 mb-0' html={p.intro2} />
        </Container>
      </section>

      {/* ───────── WHAT GREATLY SOCIAL IS ─────────
          Added because the page previously read as a generic how-to with the
          product bolted on at the end — it never said what the product was. */}
      <section className='py-5 bg-light'>
        <Container fluid='lg'>
          <Row className='justify-content-center mb-4'>
            <Col lg={8} className='text-center'>
              <h2 className='fw-bold mb-3'>{p.about.title}</h2>
              <p className='text-muted fs-5 mb-3'>{p.about.body}</p>
              <p className='text-muted fs-5 mb-0'>{p.about.body2}</p>
            </Col>
          </Row>
          <Row className='g-4 mt-2'>
            {p.about.features.map(([title, body]) => (
              <Col md={4} key={title}>
                <div className='d-flex align-items-start gap-2'>
                  <CheckCircleFill className='text-success mt-1 flex-shrink-0' />
                  <div>
                    <div className='fw-semibold'>{title}</div>
                    <div className='text-muted small'>{body}</div>
                  </div>
                </div>
              </Col>
            ))}
          </Row>
          <div className='text-center mt-5'>
            <Button variant='primary' size='lg' href='/register' className='me-2 mb-2'>
              Start using Greatly Social free
            </Button>
            <Button variant='outline-primary' size='lg' href='/plans' className='mb-2'>
              See plans from $9/month
            </Button>
          </div>
        </Container>
      </section>

      {/* ───────── STEPS ───────── */}
      <section className='py-5'>
        <Container style={{ maxWidth: 760 }}>
          <h2 className='fw-bold mb-4'>
            How to schedule {p.platform} posts with Greatly Social
          </h2>
          <ol className='fs-5 text-muted ps-3'>
            {p.steps.map(([name, text]) => (
              <li key={name} className='mb-3'>
                <strong>{name}.</strong> <Html html={text} />
              </li>
            ))}
          </ol>
        </Container>
      </section>

      {/* ───────── NATIVE vs GREATLY SOCIAL ───────── */}
      <section className='py-5 bg-light'>
        <Container fluid='lg' style={{ maxWidth: 860 }}>
          <h2 className='fw-bold mb-2'>{p.why.title}</h2>
          <p className='text-muted fs-5 mb-4'>{p.why.intro}</p>
          <div className='table-responsive'>
            <table className='table table-bordered bg-white align-middle'>
              <thead className='table-light'>
                <tr>
                  {p.why.head.map((h, i) => (
                    <th key={i} className={i === 2 ? 'text-primary' : ''}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {p.why.rows.map((row, i) => (
                  <tr key={i}>
                    {row.map((cell, j) => (
                      <Html as='td' key={j}
                        className={j === 0 ? 'fw-semibold' : j === 2 ? '' : 'text-muted'}
                        html={cell} />
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Container>
      </section>

      {/* ───────── PLATFORM SPECIFICS ───────── */}
      {p.table && (
      <section className='py-5'>
        <Container fluid='lg' style={{ maxWidth: 860 }}>
          <h2 className='fw-bold mb-2'>{p.specifics_title}</h2>
          <p className='text-muted fs-5 mb-4'>{p.specifics_intro}</p>
          <div className='table-responsive'>
            <table className='table table-bordered align-middle'>
              <thead className='table-light'>
                <tr>{p.table_head.map(h => <th key={h}>{h}</th>)}</tr>
              </thead>
              <tbody>
                {p.table.map((row, i) => (
                  <tr key={i}>
                    {row.map((cell, j) => (
                      <Html as='td' key={j}
                        className={j === 0 ? 'fw-semibold' : 'text-muted'}
                        html={cell} />
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Container>
      </section>
      )}

      {/* ───────── GOTCHAS ───────── */}
      <section className='py-5 bg-light'>
        <Container fluid='lg'>
          <Row className='justify-content-center mb-4'>
            <Col lg={8} className='text-center'>
              <h2 className='fw-bold mb-0'>What catches people out</h2>
            </Col>
          </Row>
          <Row className='g-4'>
            {p.gotchas.map(([title, body]) => (
              <Col md={4} key={title}>
                <Card className='h-100 shadow-card'>
                  <Card.Body>
                    <div className='mb-2'><ExclamationTriangleFill className='text-warning fs-4' /></div>
                    <Card.Title className='fw-semibold h6'>{title}</Card.Title>
                    <Html as='p' className='text-muted small mb-0' html={body} />
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* ───────── BULK ───────── */}
      <section className='py-5'>
        <Container style={{ maxWidth: 760 }}>
          <h2 className='fw-bold mb-3'>{p.bulk_title}</h2>
          <p className='text-muted fs-5'>{p.bulk_body}</p>
          <p className='mb-4'>
            <CheckCircleFill className='text-success me-2' />
            <a href='/bulk-posting'>See how bulk posting works &rarr;</a>
          </p>
          {p.inlineImage && (
            <figure className='mb-0'>
              <Image src={p.inlineImage} alt={p.inlineImageAlt} fluid className='rounded-3 border w-100' />
              <figcaption className='text-muted small mt-2'>{p.inlineImageAlt}</figcaption>
            </figure>
          )}
        </Container>
      </section>

      {/* ───────── SIBLING LINKS ─────────
          Added 2026-08-14. The content map showed every scheduler page was an
          orphan — no internal links pointed at them, so no authority flowed. */}
      <section className='py-5'>
        <Container fluid='lg' style={{ maxWidth: 860 }}>
          <h2 className='fw-bold h4 mb-3'>Scheduling other networks</h2>
          <Row className='g-3'>
            {platforms.filter(x => x.slug !== p.slug).map(x => (
              <Col md={4} key={x.slug}>
                <a href={`/${x.slug}`} className='d-block p-3 rounded-3 text-decoration-none h-100'
                   style={{ border: '1px solid #e5e7eb', background: '#fff' }}>
                  <div className='fw-semibold'>{x.platform} Scheduler</div>
                  <div className='text-muted small'>Schedule {x.platform} posts in advance</div>
                </a>
              </Col>
            ))}
            <Col md={4}>
              <a href='/calendar-view' className='d-block p-3 rounded-3 text-decoration-none h-100'
                 style={{ border: '1px solid #e5e7eb', background: '#fff' }}>
                <div className='fw-semibold'>Content Calendar</div>
                <div className='text-muted small'>Every network on one calendar</div>
              </a>
            </Col>
          </Row>
        </Container>
      </section>

      {/* ───────── FAQ ───────── */}
      <section className='py-5 bg-light'>
        <Container style={{ maxWidth: 760 }}>
          <h2 className='fw-bold mb-4 text-center'>{p.platform} scheduling FAQs</h2>
          <Accordion flush defaultActiveKey='0'>
            {p.faqs.map(([q, a], i) => (
              <Accordion.Item eventKey={String(i)} key={i}>
                <Accordion.Header>{q}</Accordion.Header>
                <Accordion.Body className='text-muted'>
                  <Html html={a} />
                </Accordion.Body>
              </Accordion.Item>
            ))}
          </Accordion>
        </Container>
      </section>

      {/* ───────── CTA ───────── */}
      <section className='full-bleed py-5 text-center bg-primary text-white'>
        {/* Benefits named here are the ones specific to THIS platform's problem —
            Instagram's crop failures, TikTok's ten-day ceiling — not a generic list. */}
        <div className='mx-auto px-4 text-start' style={{ maxWidth: 720 }}>
          <h2 className='fw-bold mb-3 text-center'>{p.cta.title}</h2>
          <p className='lead mb-4 text-center' style={{ color: 'rgba(255,255,255,.85)' }}>
            {p.cta.body}
          </p>
          <Row className='g-3 mb-4'>
            {p.cta.bullets.map(b => (
              <Col md={6} key={b}>
                <div className='d-flex align-items-start gap-2'>
                  <CheckCircleFill className='mt-1 flex-shrink-0' />
                  <span style={{ color: 'rgba(255,255,255,.92)' }}>{b}</span>
                </div>
              </Col>
            ))}
          </Row>
          <div className='text-center'>
            <Button variant='light' size='lg' href='/register' className='me-2 mb-2 fw-semibold'>
              Start using Greatly Social free
            </Button>
            <Button variant='outline-light' size='lg' href='/calendar-view' className='mb-2'>
              See the calendar
            </Button>
          </div>
        </div>
      </section>
    </>
  )
}
